import { useState, useEffect } from 'react'
import { FiUsers, FiSearch, FiDownload, FiPhone, FiMail, FiEdit2, FiMessageSquare, FiXCircle, FiCheckCircle, FiClock, FiAlertTriangle } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { leadsAPI } from '../services/api'
import { PageHeader } from '../ui'
import { formatearFecha } from '../utils/fechas'

const ESTADO_CONFIG = {
  trial: { label: 'Trial activo', color: 'bg-blue-100 text-blue-700' },
  trial_expiring: { label: 'Por expirar', color: 'bg-amber-100 text-amber-700' },
  expired: { label: 'Expirado', color: 'bg-red-100 text-red-700' },
  converted: { label: 'Convertido', color: 'bg-green-100 text-green-700' },
  contacted: { label: 'Contactado', color: 'bg-purple-100 text-purple-700' },
  lost: { label: 'Perdido', color: 'bg-[var(--bg-2)] text-[var(--ink-3)]' }
}

const GestionLeads = () => {
  const [leads, setLeads] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filtroEstado, setFiltroEstado] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const [editando, setEditando] = useState(null) // { id, notas }

  useEffect(() => {
    cargarDatos()
  }, [filtroEstado])

  const cargarDatos = async () => {
    setLoading(true)
    try {
      const params = {}
      if (filtroEstado !== 'todos') params.estado = filtroEstado
      if (busqueda) params.buscar = busqueda

      const [leadsRes, statsRes] = await Promise.all([
        leadsAPI.getAll(params),
        leadsAPI.getStats()
      ])
      setLeads(leadsRes.data)
      setStats(statsRes.data)
    } catch (err) {
      toast.error('Error cargando leads')
    } finally {
      setLoading(false)
    }
  }

  const buscar = () => cargarDatos()

  const actualizarEstado = async (id, estado) => {
    try {
      const data = { estado }
      if (estado === 'contacted') data.ultimo_contacto = new Date().toISOString()
      await leadsAPI.update(id, data)
      toast.success(`Lead marcado como "${ESTADO_CONFIG[estado]?.label}"`)
      cargarDatos()
    } catch (err) {
      toast.error('Error actualizando lead')
    }
  }

  const guardarNotas = async (id, notas) => {
    try {
      await leadsAPI.update(id, { notas })
      toast.success('Notas guardadas')
      setEditando(null)
      cargarDatos()
    } catch (err) {
      toast.error('Error guardando notas')
    }
  }

  const exportar = async () => {
    try {
      const res = await leadsAPI.exportCSV()
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      toast.success('CSV exportado')
    } catch (err) {
      toast.error('Error exportando')
    }
  }

  const diasDesde = (fecha) => {
    if (!fecha) return null
    const diff = Math.floor((new Date() - new Date(fecha)) / (1000 * 60 * 60 * 24))
    return diff
  }

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ L1"
        kicker="Leads"
        title="Seguimiento"
        italic="comercial."
        sub="Trials activos, contactos y conversiones."
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {[
            { label: 'Total', value: stats.total, color: 'text-[var(--ink)]' },
            { label: 'Trial', value: stats.trials_activos, color: 'text-blue-600' },
            { label: 'Por expirar', value: stats.por_expirar, color: 'text-amber-600' },
            { label: 'Expirados', value: stats.expirados, color: 'text-red-600' },
            { label: 'Convertidos', value: stats.convertidos, color: 'text-green-600' },
            { label: 'Contactados', value: stats.contactados, color: 'text-purple-600' },
            { label: 'Perdidos', value: stats.perdidos, color: 'text-[var(--ink-3)]' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-[var(--line)] p-3 text-center">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value || 0}</p>
              <p className="text-xs text-[var(--ink-3)]">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-4)] h-4 w-4" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscar()}
              placeholder="Buscar empresa, email..."
              className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] pl-10 text-sm w-full"
            />
          </div>
        </div>
        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] text-sm"
        >
          <option value="todos">Todos</option>
          {Object.entries(ESTADO_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        <button onClick={exportar} className="btn btn-secondary btn-sm flex items-center gap-1">
          <FiDownload className="h-4 w-4" /> CSV
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-[var(--line)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)] border-b border-[var(--line)]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[var(--ink-2)]">Empresa</th>
                <th className="text-left px-4 py-3 font-medium text-[var(--ink-2)]">Contacto</th>
                <th className="text-left px-4 py-3 font-medium text-[var(--ink-2)]">Plan</th>
                <th className="text-left px-4 py-3 font-medium text-[var(--ink-2)]">Trial</th>
                <th className="text-left px-4 py-3 font-medium text-[var(--ink-2)]">Estado</th>
                <th className="text-left px-4 py-3 font-medium text-[var(--ink-2)]">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8 text-[var(--ink-4)]">Cargando...</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-[var(--ink-4)]">No hay leads</td></tr>
              ) : leads.map(lead => {
                const estado = ESTADO_CONFIG[lead.estado] || ESTADO_CONFIG.trial
                const diasExp = diasDesde(lead.trial_end)
                return (
                  <tr key={lead.id} className="hover:bg-[var(--bg-2)]">
                    <td className="px-4 py-3">
                      <p className="font-medium text-[var(--ink)]">{lead.nombre_empresa}</p>
                      <p className="text-xs text-[var(--ink-3)]">{lead.nombre_admin}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-[var(--ink-2)]">
                        <FiMail className="h-3 w-3" /> {lead.email}
                      </div>
                      {lead.telefono && (
                        <div className="flex items-center gap-1 text-xs text-[var(--ink-3)] mt-0.5">
                          <FiPhone className="h-3 w-3" /> {lead.telefono}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium capitalize">{lead.plan_trial}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs">{formatearFecha(lead.trial_start)} → {formatearFecha(lead.trial_end)}</p>
                      {diasExp !== null && diasExp > 0 && lead.estado !== 'converted' && (
                        <p className="text-xs text-red-500 font-medium">Expiró hace {diasExp}d</p>
                      )}
                      {diasExp !== null && diasExp <= 0 && lead.estado === 'trial' && (
                        <p className="text-xs text-blue-500 font-medium">{Math.abs(diasExp)}d restantes</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${estado.color}`}>
                        {estado.label}
                      </span>
                      {lead.notas && (
                        <p className="text-xs text-[var(--ink-4)] mt-1 truncate max-w-[150px]" title={lead.notas}>
                          {lead.notas}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {lead.estado !== 'converted' && lead.estado !== 'lost' && (
                          <>
                            <button onClick={() => actualizarEstado(lead.id, 'contacted')}
                              className="p-1.5 rounded hover:bg-purple-100 text-[var(--ink-4)] hover:text-purple-600 transition-colors"
                              title="Marcar contactado">
                              <FiPhone className="h-4 w-4" />
                            </button>
                            <button onClick={() => actualizarEstado(lead.id, 'lost')}
                              className="p-1.5 rounded hover:bg-red-100 text-[var(--ink-4)] hover:text-red-600 transition-colors"
                              title="Marcar perdido">
                              <FiXCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button onClick={() => setEditando({ id: lead.id, notas: lead.notas || '' })}
                          className="p-1.5 rounded hover:bg-blue-100 text-[var(--ink-4)] hover:text-blue-600 transition-colors"
                          title="Añadir nota">
                          <FiMessageSquare className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de notas */}
      {editando && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h3 className="text-lg font-semibold text-[var(--ink)] mb-3">Notas del lead</h3>
            <textarea
              value={editando.notas}
              onChange={(e) => setEditando({ ...editando, notas: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] text-sm mb-4"
              placeholder="Añade notas sobre este lead..."
              autoFocus
            />
            <div className="flex gap-2">
              <button onClick={() => setEditando(null)}
                className="flex-1 btn btn-secondary">
                Cancelar
              </button>
              <button onClick={() => guardarNotas(editando.id, editando.notas)}
                className="flex-1 btn btn-primary">
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GestionLeads
